import React, {useState, useCallback, useMemo, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import moment from 'moment';
import {labels} from '../../../../../../../../util/utils';
import {useEngagementId, useUserPreferences} from '../../../../../../../../util/customHooks';
import {
	trialBalanceType,
	financialStatementType,
	defaultDateFormat,
	financialStatementCategory,
	trialBalanceCategories,
	misstatementAdjustmentGroup,
	classViewPagingOptions,
	helixImportStatus
} from '../../../../../../../../util/uiconstants';
import Utility from '../../../../../../../../util/utilityfunctions';
import {getTrialBalance, getTrialBalanceById} from '../../../../../../../../actions/TrialBalances/trialbalanceactions';
import {
	getCurrentTrialBalanceByType,
	getFinancialSatementForGrid
} from '../../../../../../../../selectors/Ares/trialBalance';
import {getCurrentMateriality} from '../../../../../../../../selectors/Ares/materiality';
import NameColumn from './NameColumn';
import DateColumn from './DateColumn';
import ClientDataTable from './ClientDataTable';
import NumberColumn from './NumberColumn';
import BalanceArrow from './BalanceArrow';
import XTE from './XTE';

const ClientDataWrapper = ({account, includeAnnotations, stEntityId}) => {
	const dispatch = useDispatch();
	const engagementId = useEngagementId();
	const userPreferences = useUserPreferences();

	const [isFirstCall, setIsFirstCall] = useState(true),
		[page, setPage] = useState(1),
		[pageSize, setPageSize] = useState(classViewPagingOptions.options[0]),
		[trialBalanceId, setTrialBalanceId] = useState(0);

	const materiality = useSelector(getCurrentMateriality),
		trialBalance = useSelector((state) => {
			const trialBalance = getCurrentTrialBalanceByType(trialBalanceType.Planning)(state);

			return trialBalance.find((tb) => tb.id === trialBalanceId && tb.accountId === account.id);
		}),
		trialBalanceData = useSelector(
			getFinancialSatementForGrid(
				[
					financialStatementType.Asset,
					financialStatementType.Liability,
					financialStatementType.Equity,
					financialStatementType.Expense,
					financialStatementType.Revenue
				],
				trialBalance?.id,
				true,
				account.id,
				includeAnnotations
			)
		);

	const formatDate = (value) => {
		if (value) {
			const dateFormat = userPreferences.dateFormat ? userPreferences.dateFormat : defaultDateFormat;
			const userFormattedDate = moment(value).format(Utility.convertUserPreferencesDateFormatToMoment(dateFormat));
			return userFormattedDate;
		} else {
			return '-';
		}
	};

	const autoGroupColumnDef = useMemo(() => {
		return {
			headerName: ``,
			cellRendererParams: {
				suppressCount: true,
				innerRenderer: NameColumn
			},
			minWidth: window.innerWidth > 1600 ? '300' : '250'
		};
	}, []);

	const getDataRow = useCallback(() => {
		if (isFirstCall) {
			return dispatch(
				getTrialBalance(
					engagementId,
					{
						filterAccountIds: account.id,
						filterImportStatus: helixImportStatus.importCompleted,
						trialBalanceTypeCategory: trialBalanceCategories.Planning,
						financialStatementCategory: financialStatementCategory.All,
						multiEntityIds: stEntityId ? stEntityId : undefined
					},
					[
						financialStatementType.Asset,
						financialStatementType.Liability,
						financialStatementType.Equity,
						financialStatementType.Expense,
						financialStatementType.Revenue
					]
				)
			).then((response) => {
				setIsFirstCall(false);
				if (response && response.length > 0) {
					const trialBalanceId = response[0].id;
					setTrialBalanceId(`${trialBalanceId}_${account.id}`);
					return dispatch(getTrialBalanceData(trialBalanceId));
				}
			});
		} else {
			// Trial balances are consolidated in the store with the account Id.
			const tbId = trialBalance && isNaN(trialBalance.id) ? trialBalance.id.split('_')[0] : trialBalance?.id;
			return tbId ? dispatch(getTrialBalanceData(tbId)) : Promise.resolve();
		}
	}, [dispatch, engagementId, account, account, page, pageSize]);

	const getTrialBalanceData = useCallback(
		(trialBalanceId) => {
			return getTrialBalanceById(
				engagementId,
				trialBalanceId,
				{
					filterAccountIds: account.id,
					financialStatementCategory: financialStatementCategory.All,
					includedMisstatementAdjustmentGroup: misstatementAdjustmentGroup.Adjustments,
					includeGLAccounts: true,
					includeFinancialStatements: true,
					isNewProvider: true,
					includepagination: true,
					page: page,
					pagesize: pageSize
				},
				[
					financialStatementType.Asset,
					financialStatementType.Liability,
					financialStatementType.Equity,
					financialStatementType.Expense,
					financialStatementType.Revenue
				],
				account.id
			);
		},
		[engagementId, account, page, pageSize]
	);

	const handlePageSizeChange = (pageSizeSelected) => {
		setPageSize(pageSizeSelected);
	};

	const handleOnPageChange = (pageSelected) => {
		setPage(pageSelected);
	};

	const columnDefs = [
		{
			field: 'analysisPeriodBalance',
			headerComponent: DateColumn,
			headerComponentParams: {
				label: labels.OARCurrentPeriod,
				date: formatDate(trialBalance?.currentPeriodDate)
			},
			cellRenderer: 'numberColumn',
			maxWidth: window.innerWidth > 1600 ? '180' : '150'
		},
		{
			field: 'comparativePeriodBalance',
			headerComponent: DateColumn,
			headerComponentParams: {
				label: labels.OARComparative1Period,
				date: formatDate(trialBalance?.comparativePeriodDate)
			},
			cellRenderer: 'numberColumn',
			maxWidth: window.innerWidth > 1600 ? '180' : '150'
		},
		{
			field: 'analysisComparativeBalanceChangePercent',
			headerComponent: DateColumn,
			headerComponentParams: {
				label: labels.OARPercentageChangeFrom,
				date: formatDate(trialBalance?.comparativePeriodDate)
			},
			cellRenderer: 'balanceArrow',
			maxWidth: window.innerWidth > 1600 ? '150' : '120'
		},
		{
			field: 'secondComparativePeriodBalance',
			headerComponent: DateColumn,
			headerComponentParams: {
				label: labels.OARComparative2Period,
				date: formatDate(trialBalance?.otherComparativePeriodDate)
			},
			cellRenderer: 'numberColumn',
			maxWidth: window.innerWidth > 1600 ? '180' : '150',
			hide: !trialBalance?.otherComparativePeriodDate
		},
		{
			field: 'analysisSecondComparativeBalanceChangePercent',
			headerComponent: DateColumn,
			headerComponentParams: {
				label: labels.OARPercentageChangeFrom,
				date: formatDate(trialBalance?.othercomparativeperioddate)
			},
			cellRenderer: 'balanceArrow',
			maxWidth: window.innerWidth > 1600 ? '150' : '120',
			hide: !trialBalance?.otherComparativePeriodDate
		},
		{
			field: 'analysisPeriodBalance',
			headerComponent: DateColumn,
			headerComponentParams: {
				label: labels.xTELabel
			},
			cellRenderer: 'xTE',
			cellRendererParams: {
				materiality: materiality
			},
			maxWidth: window.innerWidth > 1600 ? '150' : '120'
		}
	];

	return (
		<StyeledClientDataWrapper className="StyeledClientDataWrapper">
			{trialBalanceData ? (
				<ClientDataTable
					columnDefs={columnDefs}
					frameworkComponents={{
						balanceArrow: BalanceArrow,
						numberColumn: NumberColumn,
						xTE: XTE
					}}
					autoGroupColumnDef={autoGroupColumnDef}
					getDataRow={getDataRow}
					trialBalanceData={trialBalanceData}
					page={page}
					pageSize={pageSize}
					pageCount={trialBalance?.fifthLevelFinancialStatements?.pages}
					onPageSizeChanged={handlePageSizeChange}
					onPageChanged={handleOnPageChange}
					optionsPerPage={classViewPagingOptions.options}
				/>
			) : (
				<section className="noDataAvailable">{labels.noClientDataAvailable}</section>
			)}
		</StyeledClientDataWrapper>
	);
};

export default ClientDataWrapper;

const StyeledClientDataWrapper = styled.section`
	display: flex;
	flex-direction: column;
	height: calc(100% - var(--px-100));
`;
